import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import _, { isFunction } from 'lodash';
import moment from 'moment';
import { bedStatusAPI, eadmissionAPI, systemAPI } from '../../apis';
import { LOGIN_INFO, WARD_INFO } from '../../comm/constant';
import { closeFullScreen, openFullScreen, updateActionableSettings, updateLoginInfoAction } from '../../redux/actions';
import { addLoginAction } from '../check';
import { mutationUpdateOtherLoginInfo } from '../handleUpdateOtherLoginInfo';
import { alertActionable } from '../actionable';
import { useQuery } from '../useQuery';
import { userRoleFunc, userRoleList } from '../userRole';

export const useActionable = () => {
  const location = useLocation();
  const { actionableMode } = useQuery();
  const dispatch = useDispatch();

  const actionable = useMemo(() => {
    const isActionable = location.pathname.includes('actionable') || actionableMode === '1';
    if (isActionable) {
      dispatch(openFullScreen());
    } else {
      dispatch(closeFullScreen());
    }
    return isActionable;
  }, [location, actionableMode]);

  return actionable;
};

export const useGetLoginInfo = () => {
  const loginInfo = useSelector((state) => state.loginInfo);
  return loginInfo;
};

export const useAutoLogout = (open) => {
  const timeoutRef = useRef(null);
  const dispatch = useDispatch();

  const handleLogout = () => {
    localStorage.removeItem(LOGIN_INFO);
    mutationUpdateOtherLoginInfo();
    const wardinfo = JSON.parse(localStorage.getItem(WARD_INFO));
    dispatch(updateLoginInfoAction({ flag: false, ...wardinfo }));
    addLoginAction();
  };

  const resetTimer = () => {
    // 清除已有的计时器
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // 设置新的计时器
    timeoutRef.current = setTimeout(() => {
      console.log('用户已 1 分钟没有操作');
      handleLogout();
    }, 60000); // 60 秒
  };

  useEffect(() => {
    if (open) {
      // 监听各种用户交互事件
      const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

      // 在每个事件上绑定 resetTimer 函数
      events.forEach((event) => {
        window.addEventListener(event, resetTimer);
      });

      // 初始设置计时器
      resetTimer();

      // 组件卸载时清除事件监听和计时器
      return () => {
        events.forEach((event) => {
          window.removeEventListener(event, resetTimer);
        });
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [open]);
};

export const playAudio = async (src, callback) => {
  try {
    const audio = document.getElementById('actionable_alert');
    audio.src = src;
    await audio.play();
    callback?.();
  } catch (error) {
    console.log('Audio play failed:', error);
  }
};

export const useActionableTicketReminder = (alertType = 'msg', tableName, deps = []) => {
  const loginInfo = useGetLoginInfo();
  const [audio, setAudio] = useState({ alertWardSetting: [], audioFile: [] });
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const audioRef = useRef(audio);
  let getAudioFlag = false;
  const AdmissionWorkflowReducer = useSelector((state) => state.AdmissionWorkflowReducer);
  const [needRefresh, setNeedRefresh] = useState(false);
  // logout的状态也需要能看到admission
  const showAdmission = userRoleFunc?.getIsShowAdmission() || !loginInfo?.flag;

  // 获取音频文件和设置
  const getTicketAudioFile = async (hospId) => {
    try {
      const params = {
        isWard: tableName === 'BS',
        hospId,
      };
      const { result } = await eadmissionAPI.getTicketAudioFile(params, { isHideLoading: true });
      setAudio(result);
      audioRef.current = result;
      getAudioFlag = true;
      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const updateUserRoleTypeId = async () => {
    let roleList = [];
    try {
      const { result } = await systemAPI.getSysUserRole({ corpId: loginInfo.corpID });
      if (Array.isArray(result)) {
        roleList = result;
      }
    } catch (error) {}
    userRoleList.setGlobalUserRoleTypeId(roleList, loginInfo.hospitalId, loginInfo.wardId, loginInfo.userId);
  };

  const getTickets = async (loginInfo) => {
    try {
      const ADMISSION_BROWSER_ALERT_SETTING = localStorage.getItem('ADMISSION_BROWSER_ALERT_SETTING');
      const minute = moment().minutes();
      let params = { tableName };
      if (ADMISSION_BROWSER_ALERT_SETTING === 'true') {
        const specialtys = JSON.parse(localStorage.getItem('ADMISSION_FILTER'))?.specialtys ?? [];
        params.specialtys = _.map(specialtys, (item) => item.value);
      }
      const { result } = await eadmissionAPI.getTickets(params, { isHideLoading: true, withoutToken: true });
      setAllTickets(result);
      let audioInfo = null;

      if (getAudioFlag) {
        audioInfo = audioRef.current;
      } else {
        // 获取 alert setting
        audioInfo = await getTicketAudioFile(loginInfo.hospitalId);
      }

      // 缓存里的记录
      const cacheTickets = JSON.parse(sessionStorage.getItem(`ACTIONABLE_TICKET_${tableName}`) ?? '{}');
      if (minute % 5 === 0) {
        const repeatTime = sessionStorage.getItem(`ACTIONABLE_REPEATE_${tableName}`);
        if (repeatTime === moment().format('YYYY-MM-DD HH:mm')) {
          return;
        }
        const updatedTickets = _.filter(
          result,
          (item) =>
            (item.count > 0 &&
              // 重复
              _.some(
                audioInfo.alertWardSetting,
                (audioSettingItem) =>
                  audioSettingItem.actionableAlert === item.ticketType &&
                  minute % audioSettingItem.audioAlertRepeatEvery === 0
              )) ||
            // 更新
            _.some(
              cacheTickets[loginInfo.wardId],
              (cacheItem) =>
                cacheItem.tableName === item.tableName &&
                cacheItem.ticketType === item.ticketType &&
                cacheItem.lastUpdateDate !== item.lastUpdateDate &&
                Boolean(item.lastUpdateDate)
            )
        );
        setTickets(updatedTickets);
        sessionStorage.setItem(`ACTIONABLE_REPEATE_${tableName}`, moment().format('YYYY-MM-DD HH:mm'));
      } else {
        // 对比获得更新的ticket
        const updatedTickets = _.filter(result, (item) =>
          _.some(
            cacheTickets[loginInfo.wardId],
            (cacheItem) =>
              cacheItem.tableName === item.tableName &&
              cacheItem.ticketType === item.ticketType &&
              cacheItem.lastUpdateDate !== item.lastUpdateDate &&
              Boolean(item.lastUpdateDate)
          )
        );
        setTickets(updatedTickets);
      }

      // 更新缓存
      sessionStorage.setItem(
        `ACTIONABLE_TICKET_${tableName}`,
        JSON.stringify({
          ...cacheTickets,
          [loginInfo.wardId]: result,
        })
      );
    } catch (error) {
      console.log(error);
    }
  };

  // 启动所有逻辑的定时器
  useEffect(() => {
    if (AdmissionWorkflowReducer && showAdmission) {
      (async () => {
        // 在call getTickets 之前先call一次userRole的api更新 userRoleTypeId，以防 roleTypeId 变化后 tickets 接口权限问题
        if (loginInfo?.flag) {
          await updateUserRoleTypeId();
        }
        getTickets(loginInfo);
      })();
      const interval = setInterval(() => {
        getTickets(loginInfo);
      }, 1000 * 20);

      return () => clearInterval(interval);
    }
  }, [loginInfo, AdmissionWorkflowReducer, showAdmission, ...deps]);

  // 获取需要播放的音频
  const alertTickets = useMemo(() => {
    if (tickets.length > 1) {
      return _.find(audio.audioFile, (item) => item.actionableAlert === 'MAA');
    } else if (tickets.length === 1) {
      return _.find(audio.audioFile, (item) => item.actionableAlert === tickets[0].ticketType);
    } else {
      return null;
    }
  }, [tickets, audio]);

  // 最后根据alertType做出相应的处理, msg = 既提示又播放声音，audio = 仅播放声音
  useEffect(() => {
    if (AdmissionWorkflowReducer && showAdmission) {
      const mute = JSON.parse(localStorage.getItem('ACTIONABLE_SETTING'))?.mute;
      if (alertTickets) {
        setNeedRefresh((o) => !o);
        if (alertType === 'msg') {
          alertActionable('msg', {
            path: `/actionable${tableName === 'BS' ? 'BS' : ''}?type=${tableName}`,
          });
        }
        if (!mute) {
          playAudio(`data:${alertTickets.mineType};base64,${alertTickets.audioFile}`, () => setTickets([]));
        }
      }
    }
  }, [alertTickets, AdmissionWorkflowReducer, showAdmission]);

  return [allTickets, needRefresh];
};

export const useActionableSettings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.ActionableSettingsReducer);

  const updateActionableSetting = (props) => {
    if (isFunction(props)) {
      dispatch(updateActionableSettings(props(settings)));
    } else {
      dispatch(updateActionableSettings(props));
    }
  };

  return [settings, updateActionableSetting];
};

export const useActionableAudioOpen = () => {
  useEffect(() => {
    // 监听各种用户交互事件
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    const startAudio = async () => {
      try {
        const audio = document.getElementById('actionable_alert');
        const isPlaying = !audio.paused;
        if (isPlaying) {
          return;
        }
        audio.src = '';
        await audio.play();
      } catch (error) {}
    };

    // 在每个事件上绑定函数
    events.forEach((event) => {
      window.addEventListener(event, startAudio);
    });

    // 组件卸载时清除事件监听
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, startAudio);
      });
    };
  }, []);
};

export const useEnableServiceOrder = () => {
  const rouList = useSelector((state) => state.rouList);
  const [orderServiceInfo, setOrderServiceInfo] = useState({
    enableYn: false,
    showButtonYn: false,
    orderServiceBy: '',
  });

  const getWardCleansingEnable = async () => {
    try {
      const { result } = await bedStatusAPI.getWardCleansingEnable();
      setOrderServiceInfo({
        enableYn: result?.enableYn === 1,
        showButtonYn: result?.showButtonYn === 1,
        orderServiceBy: result?.orderServiceBy,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const updateWardCleansingEnable = async (name, val) => {
    try {
      setOrderServiceInfo((prev) => ({
        ...prev,
        [name]: val,
      }));
      await bedStatusAPI.updateWardCleansingEnable({
        enableYn: orderServiceInfo?.enableYn ? 1 : 0,
        showButtonYn: orderServiceInfo?.showButtonYn ? 1 : 0,
        [name]: val ? 1 : 0,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getWardCleansingEnable();
  }, [rouList]);

  return {
    enableOrderService: orderServiceInfo?.enableYn,
    showOrderServiceBtn: orderServiceInfo?.showButtonYn,
    orderServiceBy: orderServiceInfo?.orderServiceBy,
    updateWardCleansingEnable: updateWardCleansingEnable,
  };
};
